import type { ImageUploadBody } from '@hewey/shared'

// Resize + re-encode a picked photo to a small JPEG entirely on-device, before
// upload. This is what keeps stored images tiny (so the authed image proxy
// stays under Lambda's 6MB response cap), fixes iPhone HEIC (canvas always
// emits JPEG), and strips GPS/EXIF (only the pixels survive the canvas round-
// trip). Returns the base64 body the upload endpoint expects — no `data:`
// prefix.
//
// `imageOrientation: 'from-image'` is correctness-critical: it bakes the EXIF
// rotation into the bitmap so a portrait phone photo isn't stored sideways.
// Verify on a real iPhone portrait shot.
export const downscaleImage = async (
	file: File,
	maxDim = 1600,
	quality = 0.82,
): Promise<ImageUploadBody> => {
	const bitmap = await createImageBitmap(file, {
		imageOrientation: 'from-image',
	})

	// Scale to fit within maxDim×maxDim, never upscaling a smaller image.
	const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height))
	const width = Math.max(1, Math.round(bitmap.width * scale))
	const height = Math.max(1, Math.round(bitmap.height * scale))

	const canvas = document.createElement('canvas')
	canvas.width = width
	canvas.height = height
	const ctx = canvas.getContext('2d')
	if (!ctx) throw new Error('Canvas 2D context unavailable')
	ctx.drawImage(bitmap, 0, 0, width, height)
	bitmap.close()

	const blob = await new Promise<Blob | null>((resolve) => {
		canvas.toBlob(resolve, 'image/jpeg', quality)
	})
	if (!blob) throw new Error('Image encode failed')

	const dataUrl = await new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.addEventListener('load', () => resolve(reader.result as string))
		reader.addEventListener('error', () =>
			reject(reader.error ?? new Error('read failed')),
		)
		reader.readAsDataURL(blob)
	})

	// Strip the `data:image/jpeg;base64,` prefix — the endpoint wants raw base64.
	const comma = dataUrl.indexOf(',')
	const data = comma === -1 ? dataUrl : dataUrl.slice(comma + 1)

	return { data, contentType: 'image/jpeg' }
}
